import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { ValidationService } from './validation.service';

import { ValidationStateService } from './validation-state.service';
import { inject, Injectable, Signal } from '@angular/core';
import { CompassRouteParent } from '@r1-compass-library/models';
import { SidenavValidationRegistry } from './sidenav-validation-registry.service';
import { SectionNames } from './section-names.model';

export type ModelFormMap<SectionName extends SectionNames> = Record<SectionName, AbstractControl>;

/**
 * @description Top level component of a given domain
 */
export interface SyncValidationStateTopLevelComponent<SectionName extends SectionNames, DomainModel, Context = void> {
    registerInvalidSections(routeData: CompassRouteParent, invalidSections: SectionName[]): void;
    validateSectionsOnLoad(
        model: DomainModel | undefined,
        validationService: ValidationService<SectionName, DomainModel, ModelFormMap<SectionName>, Context>,
        validationStateService: ValidationStateService<SectionName>,
        context: Context
    ): void;
}

/**
 * @description Individual section's component of a given domain
 */
export interface SyncValidationStateSection<SectionName extends SectionNames, DomainModel, Context = void> {
    subscribeToValidationState(
        form: FormGroup,
        sectionName: SectionName,
        validationService: ValidationService<SectionName, DomainModel, ModelFormMap<SectionName>, Context>,
        validationStateService: ValidationStateService<SectionName>
    ): Subscription;

    /**
     * @example
        // ts
        disableSave = computed<boolean>(() =>
            this.#validationStateService.shouldDisableSave(
                this.#entityStore.entity(),
                this.entityPageForm,
                this.#validationStateService.invalidSections(),
                'entityName'
                // (optional, only needed for plays) pass the `playLogicException` flag here. Like the definition has custom boolean for disabling.
            )
        )
        // html
        <button type="submit" [disabled]="disableSave()">Save</button>
    */
    disableSave: Signal<boolean>;
}

@Injectable({ providedIn: 'root' })
export class SyncValidationStateService {
    readonly #sidenavValidationRegistry = inject(SidenavValidationRegistry);

    /**
     * @description Tracks section's form validity to sync validation state.
     * Called in an `effect` or `ngOnInit`, depending on when form is available/last mutated.
     * For interface of {@link SyncValidationStateSection}
     */
    public subToValidationState<SectionName extends SectionNames, Model, Context = void>(
        form: FormGroup,
        sectionName: SectionName,
        validationService: ValidationService<SectionName, Model, ModelFormMap<SectionName>, Context>,
        validationStateService: ValidationStateService<SectionName>,
        getContext?: () => Context | undefined
    ) {
        const validate = () => {
            // Without the context (e.g. still loading), validation would be wrong, so skip it
            // But if `getContext` not needed, can pass
            const context = getContext ? getContext() : undefined;
            if (context === undefined && getContext) return;
            const validationStatus = validationService.validateSection(form, sectionName, context);
            validationStateService.setSectionInvalid(sectionName, validationStatus.erroredSections.has(sectionName));
        };

        return form.events.pipe(debounceTime(250), distinctUntilChanged()).subscribe(() => {
            validate();
        });
    }

    /**
     * @description Tracks invalid sections, use in an `effect`
     */
    public registerInvalidSections<SectionName extends SectionNames>(
        routeData: CompassRouteParent,
        invalidSections: SectionName[]
    ) {
        this.#sidenavValidationRegistry.register(routeData.path, invalidSections);
    }

    /**
     * @description Validates the various sections on load, use in an `effect`
     */
    public validateSectionsOnLoad<SectionName extends SectionNames, Model, Context = void>(
        model: Model | undefined,
        validationService: ValidationService<SectionName, Model, ModelFormMap<SectionName>, Context>,
        validationStateService: ValidationStateService<SectionName>,
        context?: Context,
        isRequiredContext = false
    ) {
        if (!model || (isRequiredContext && context == null)) {
            return;
        }
        const status = validationService.validateModel(model, context);
        validationStateService.setSectionsFromStatus(status);
    }
}
